"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShowCard } from "./show-card";
import { ShowFilters } from "./show-filters";
import LoadingSpinner from "@/components/loading-spinner";
import { ROLES, type RoleId } from "@/lib/constants/roles";

interface Show {
  id: number;
  venue_id: number;
  duration_minutes: number;
  min_participants: number;
  max_participants: number;
  status?: string;
  status_id?: number;
  statuses?: {
    id: number;
    name: string;
  };
  customer_types?: {
    id: number;
    name: string;
  };
  show_details?: Array<{
    id: number;
    name: string;
    details: string;
    duration?: number;
    lang: string;
  }>;
  show_studios?: Array<{
    studio_id: number;
    studios?: {
      id: number;
      name: string;
    };
  }>;
  show_medias?: Array<{
    id: number;
    url: string;
    type: string;
    is_primary: boolean;
  }>;
  show_prices?: Array<{
    id?: number;
    price_type: string;
    amount: number;
    currency: string;
    pricing?: {
      id: number;
      price_type: string;
      amount: number;
      currency: string;
    };
  }>;
}

interface Studio {
  id: number;
  name: string;
}

interface ShowListProps {
  venueId: number;
  userRoleId?: RoleId;
}

export function ShowList({
  venueId,
  userRoleId = ROLES.BOOKING_STAFF,
}: ShowListProps) {
  const router = useRouter();
  console.log(
    "ShowList component rendered with venueId:",
    venueId,
    "userRoleId:",
    userRoleId,
  );

  const [shows, setShows] = useState<Show[]>([]);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchValue, setSearchValue] = useState("");
  const [statusValue, setStatusValue] = useState("all");
  const [studioValue, setStudioValue] = useState("all");
  const [sortValue, setSortValue] = useState("newest");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });

  const fetchShows = async () => {
    try {
      const params = new URLSearchParams();
      if (statusValue !== "all") params.append("status", statusValue);
      if (studioValue !== "all") params.append("studioId", studioValue);
      if (searchValue) params.append("search", searchValue);
      if (priceRange.min) params.append("minPrice", priceRange.min);
      if (priceRange.max) params.append("maxPrice", priceRange.max);

      const url = `/api/venues/${venueId}/shows?${params}`;
      console.log("Fetching shows from:", url);

      const response = await fetch(url);
      console.log("Shows response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Shows API error:", response.status, errorText);
        throw new Error(`Failed to fetch shows: ${response.status}`);
      }

      const data = await response.json();
      console.log("Shows API response:", data);
      setShows(data.shows || []);
    } catch (error) {
      console.error("Error fetching shows:", error);
    }
  };

  const fetchStudios = async () => {
    try {
      console.log("Fetching studios for venue:", venueId);
      const response = await fetch(`/api/venues/${venueId}/studios`);
      console.log("Studios response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Studios fetch failed:", response.status, errorText);
        throw new Error("Failed to fetch studios");
      }

      const data = await response.json();
      console.log("Fetched studios data:", data);
      console.log("Number of studios:", data.studios?.length || 0);
      setStudios(data.studios || []);
    } catch (error) {
      console.error("Error fetching studios:", error);
      setStudios([]); // Set empty array on error
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchShows(), fetchStudios()]);
      setLoading(false);
    };

    loadData();
  }, [venueId, statusValue, studioValue, searchValue, priceRange]);

  const handleCreateShow = () => {
    // TODO: Implement show creation dialog or redirect to proper creation page
    console.log("Create show functionality needs to be implemented");
  };

  const handleDeleteShow = async (showId: number) => {
    try {
      const response = await fetch(`/api/venues/${venueId}/shows/${showId}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete show");

      await fetchShows();
    } catch (error) {
      console.error("Error deleting show:", error);
    }
  };

  const sortShows = (shows: Show[]) => {
    switch (sortValue) {
      case "name":
        return [...shows].sort((a, b) => {
          const nameA = a.show_details?.[0]?.name || "";
          const nameB = b.show_details?.[0]?.name || "";
          return nameA.localeCompare(nameB);
        });
      case "popular":
        // Would sort by booking count - placeholder for now
        return shows;
      case "price_low":
        return [...shows].sort((a, b) => {
          const priceA =
            a.show_prices?.find((p) => p.price_type === "base")?.amount || 0;
          const priceB =
            b.show_prices?.find((p) => p.price_type === "base")?.amount || 0;
          return priceA - priceB;
        });
      case "price_high":
        return [...shows].sort((a, b) => {
          const priceA =
            a.show_prices?.find((p) => p.price_type === "base")?.amount || 0;
          const priceB =
            b.show_prices?.find((p) => p.price_type === "base")?.amount || 0;
          return priceB - priceA;
        });
      case "newest":
      default:
        return shows; // Already sorted by created_at desc from API
    }
  };

  const sortedShows = sortShows(shows);
  const canCreateShows =
    userRoleId === ROLES.SUPER_ADMIN || userRoleId === ROLES.BOOKING_STAFF;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Shows</h1>
          <p className="text-muted-foreground">
            Manage game shows for this venue
          </p>
        </div>

        {canCreateShows && (
          <Button onClick={handleCreateShow}>
            <Plus className="h-4 w-4 mr-2" />
            Create Show
          </Button>
        )}
      </div>

      <ShowFilters
        onSearchChange={setSearchValue}
        onStatusChange={setStatusValue}
        onStudioChange={setStudioValue}
        onPriceRangeChange={(min, max) => setPriceRange({ min, max })}
        onSortChange={setSortValue}
        studios={studios}
        searchValue={searchValue}
        statusValue={statusValue}
        studioValue={studioValue}
        sortValue={sortValue}
        priceRange={priceRange}
      />

      {sortedShows.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Plus className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No shows found
          </h3>
          <p className="text-gray-500 mb-4">
            {canCreateShows
              ? "Get started by creating your first show."
              : "No shows match your current filters."}
          </p>
          {canCreateShows && (
            <Button onClick={handleCreateShow}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Show
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedShows.map((show) => (
            <ShowCard
              key={show.id}
              show={show}
              venueId={venueId}
              onDelete={
                userRoleId === ROLES.SUPER_ADMIN ||
                userRoleId === ROLES.BOOKING_STAFF
                  ? handleDeleteShow
                  : undefined
              }
              userRoleId={userRoleId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
